import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  addMonths,
  format,
  isAfter,
  isSameDay,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, DayFlag, UI } from "react-day-picker";

const PRESETS = [
  { id: "7d", label: "Last 7 days", days: 7 },
  { id: "30d", label: "Last 30 days", days: 30 },
  { id: "90d", label: "Last 90 days", days: 90 },
];

const dayPickerClassNames = {
  [UI.Root]: "w-full",
  [UI.Months]: "w-full",
  [UI.Month]: "w-full px-3 pb-3 pt-2",
  [UI.MonthCaption]: "hidden",
  [UI.CaptionLabel]: "hidden",
  [UI.Nav]: "hidden",
  [UI.PreviousMonthButton]: "hidden",
  [UI.NextMonthButton]: "hidden",
  [UI.Chevron]: "h-0 w-0",
  [UI.MonthGrid]: "w-full table-fixed border-collapse",
  [UI.Weekdays]: "border-0",
  [UI.Weekday]:
    "pb-2 text-center text-[10.5px] font-semibold uppercase tracking-[0.06em] text-(--text-muted)",
  [UI.Week]: "h-8",
  [UI.Day]: "h-8 p-0 text-center align-middle",
  [UI.DayButton]:
    "mx-auto flex h-7 w-7 items-center justify-center rounded-md border-0 bg-transparent p-0 text-[12.5px] font-medium text-(--text-primary) transition hover:bg-(--surface-2)",
  [DayFlag.outside]: "[&>button]:text-(--text-faint)",
  [DayFlag.today]: "[&>button]:font-bold [&>button]:text-(--brand-primary)",
};

const modifierClassNames = {
  selected_single:
    "[&>button]:bg-(--brand-primary) [&>button]:text-(--brand-primary-contrast)",
  selected_start:
    "rounded-l-md bg-(--pastel-violet) [&>button]:bg-(--brand-primary) [&>button]:text-(--brand-primary-contrast)",
  selected_middle: "bg-(--pastel-violet) [&>button]:text-(--text-primary)",
  selected_end:
    "rounded-r-md bg-(--pastel-violet) [&>button]:bg-(--brand-primary) [&>button]:text-(--brand-primary-contrast)",
};

const POPOVER_WIDTH = 460;
const STACK_BELOW = 420;
const GUTTER = 16; // kept between the popover and the viewport edges
const OFFSET = 8; // gap between the trigger and the popover

const presetRange = (days) => {
  const to = startOfDay(new Date());
  const from = startOfDay(subDays(to, days - 1));
  return { from, to };
};

const matchPreset = (range) => {
  for (const preset of PRESETS) {
    const candidate = presetRange(preset.days);
    if (
      isSameDay(candidate.from, range.from) &&
      isSameDay(candidate.to, range.to)
    ) {
      return preset;
    }
  }
  return null;
};

const fmtLabel = (range) => {
  if (isSameDay(range.from, range.to)) {
    return format(range.from, "MMM d, yyyy");
  }
  const sameYear = range.from.getFullYear() === range.to.getFullYear();
  return sameYear
    ? `${format(range.from, "MMM d")} – ${format(range.to, "MMM d, yyyy")}`
    : `${format(range.from, "MMM d, yyyy")} – ${format(range.to, "MMM d, yyyy")}`;
};

// `align` picks which edge of the trigger the popover hangs from: "right" (the
// default) for a picker at the end of a toolbar, "left" for one in a left-hand
// side panel, where a right-anchored popover would open off-screen.
export default function DateRangePicker({
  value,
  onChange,
  className = "",
  align = "right",
}) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [visibleMonth, setVisibleMonth] = useState(
    startOfMonth(value.to ?? new Date()),
  );
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const popoverId = useId();
  // Where the popover sits, in viewport px ({ top, left, width, maxHeight? });
  // null until measured. It stays in the DOM right after its trigger (so Tab
  // goes trigger → popover, and focus leaving the root closes it) but is
  // positioned `fixed`, so the page's scroll container cannot clip it where a
  // narrow viewport leaves less room beside the trigger than it needs. (A fixed
  // element is placed against a transformed ancestor instead of the viewport;
  // no caller puts the picker inside one.)
  const [position, setPosition] = useState(null);

  // Under ~420px the presets go above the calendar instead of beside it, so
  // the month grid keeps its width rather than being crushed.
  const stacked = position !== null && position.width < STACK_BELOW;

  const matchedPreset = useMemo(() => matchPreset(value), [value]);
  const triggerLabel = matchedPreset ? matchedPreset.label : fmtLabel(value);

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setVisibleMonth(startOfMonth(value.to ?? new Date()));
      setAnchor(null);
    }
    prevOpenRef.current = open;
  }, [open, value.to]);

  // Horizontally: hang from the trigger's `align` edge, clamped inside the
  // viewport with a 16px gutter; 460px wide where that fits, the viewport
  // otherwise. Vertically: below the trigger if it fits, else above, else on
  // the roomier side with a max height (the popover then scrolls). Re-measured
  // on resize, on any scroll (so it follows the trigger) and when the layout
  // stacks, which changes its height.
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }
    const measure = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(POPOVER_WIDTH, window.innerWidth - GUTTER * 2);
      const wanted = align === "left" ? rect.left : rect.right - width;
      const left = Math.min(
        Math.max(wanted, GUTTER),
        window.innerWidth - GUTTER - width,
      );
      const height = popoverRef.current?.scrollHeight ?? 0;
      const below = window.innerHeight - rect.bottom - OFFSET - GUTTER;
      const above = rect.top - OFFSET - GUTTER;
      let vertical;
      if (height <= below) vertical = { top: rect.bottom + OFFSET };
      else if (height <= above) vertical = { top: rect.top - OFFSET - height };
      else if (above > below) vertical = { top: GUTTER, maxHeight: above };
      else vertical = { top: rect.bottom + OFFSET, maxHeight: below };
      setPosition({ left, width, ...vertical });
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, align, stacked]);

  useEffect(() => {
    if (!open) return undefined;
    const handle = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  // Closing after a choice or Escape hands focus back to the trigger, so
  // keyboard users stay where they were.
  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape" && open) {
      e.stopPropagation();
      close();
    }
  };

  // Focus moving anywhere outside the picker (Tab past its last control, or
  // into an overlay that opened) closes it. A null relatedTarget is a click on
  // something unfocusable, which the mousedown handler already covers.
  const handleBlur = (e) => {
    if (
      open &&
      e.relatedTarget !== null &&
      !rootRef.current?.contains(e.relatedTarget)
    ) {
      setOpen(false);
    }
  };

  const applyPreset = (preset) => {
    onChange(presetRange(preset.days));
    close();
  };

  const handleDayClick = (day) => {
    const startOfClicked = startOfDay(day);
    if (anchor === null) {
      setAnchor(startOfClicked);
      onChange({ from: startOfClicked, to: startOfClicked });
      return;
    }
    const next = isAfter(anchor, startOfClicked)
      ? { from: startOfClicked, to: anchor }
      : { from: anchor, to: startOfClicked };
    onChange(next);
    setAnchor(null);
    close();
  };

  const isSingleDate = isSameDay(value.from, value.to);
  const modifiers = isSingleDate
    ? { selected_single: value.from }
    : {
        selected_start: value.from,
        selected_middle: { after: value.from, before: value.to },
        selected_end: value.to,
      };

  return (
    <div
      ref={rootRef}
      className={`relative ${className}`.trim()}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        className={`inline-flex items-center gap-2 rounded-(--radius-md) border bg-(--surface-1) px-[14px] py-[9px] text-[13px] font-semibold text-(--text-primary) transition-colors ${
          open
            ? "border-(--brand-primary)"
            : "border-(--border-strong) hover:border-(--brand-primary)"
        }`}
      >
        <Calendar
          size={15}
          strokeWidth={1.8}
          className="text-(--text-secondary)"
        />
        <span>{triggerLabel}</span>
      </button>

      {open && (
        <div
          ref={popoverRef}
          id={popoverId}
          role="dialog"
          aria-label="Choose date range"
          className={`fixed z-40 flex overflow-x-hidden overflow-y-auto rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-1) ${
            stacked ? "flex-col" : "flex-row"
          }`}
          // Until measured: laid out at full width but invisible, so the
          // first measurement can read its height.
          style={
            position ?? {
              visibility: "hidden",
              top: 0,
              left: 0,
              width: POPOVER_WIDTH,
            }
          }
        >
          <div
            className={`flex shrink-0 gap-1 border-(--border-subtle) bg-(--surface-2) p-3 ${
              stacked
                ? "flex-row flex-wrap items-center border-b"
                : "w-[140px] flex-col border-r"
            }`}
          >
            <div className="px-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-(--text-muted)">
              Quick range
            </div>
            {PRESETS.map((preset) => {
              const active = matchedPreset?.id === preset.id;
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => applyPreset(preset)}
                  className={`rounded-md border-0 bg-transparent px-2 py-[7px] text-left text-[13px] transition-colors ${
                    active
                      ? "bg-(--pastel-violet) font-bold text-(--brand-primary)"
                      : "font-semibold text-(--text-secondary) hover:bg-(--surface-1) hover:text-(--text-primary)"
                  }`}
                >
                  {preset.label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => {
                setVisibleMonth(startOfMonth(value.to ?? new Date()));
                setAnchor(null);
              }}
              className={`mt-1 rounded-md border-0 bg-transparent px-2 py-[7px] text-left text-[13px] transition-colors ${
                matchedPreset === null
                  ? "bg-(--pastel-violet) font-bold text-(--brand-primary)"
                  : "font-semibold text-(--text-secondary) hover:bg-(--surface-1) hover:text-(--text-primary)"
              }`}
            >
              Custom range
            </button>
          </div>

          <div className="flex-1">
            <div className="flex items-center justify-between border-b border-(--border-subtle) px-3 py-2">
              <button
                type="button"
                onClick={() => setVisibleMonth((m) => subMonths(m, 1))}
                aria-label="Previous month"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border-0 bg-transparent text-(--text-secondary) hover:bg-(--surface-2) hover:text-(--text-primary)"
              >
                <ChevronLeft size={15} strokeWidth={2} />
              </button>
              <p className="text-[13px] font-bold text-(--text-primary)">
                {format(visibleMonth, "MMMM yyyy")}
              </p>
              <button
                type="button"
                onClick={() => setVisibleMonth((m) => addMonths(m, 1))}
                aria-label="Next month"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border-0 bg-transparent text-(--text-secondary) hover:bg-(--surface-2) hover:text-(--text-primary)"
              >
                <ChevronRight size={15} strokeWidth={2} />
              </button>
            </div>

            <DayPicker
              fixedWeeks
              showOutsideDays
              hideNavigation
              month={visibleMonth}
              onMonthChange={setVisibleMonth}
              onDayClick={handleDayClick}
              modifiers={modifiers}
              modifiersClassNames={modifierClassNames}
              classNames={dayPickerClassNames}
            />
          </div>
        </div>
      )}
    </div>
  );
}
